import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { Container, Row, Col, Button, ButtonGroup, Input, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import axios from 'src/utils/axios';
import Spinner from 'src/components/spinner';
import moment from 'moment';

// Scoring, hearting (for the slideshow) and archiving (removing from the "to score" queue) are three
// separate actions. Scoring a photo no longer makes it vanish, so a mis-click can always be put right.
export default function EventScoring() {
	const { id: event_id } = useParams();
	const [show, setShow] = useState('queue'); // queue | archived | all
	const [images, setImages] = useState(null);
	const [focusedId, setFocusedId] = useState(null);
	const [score, setScore] = useState(1);
	const [busy, setBusy] = useState(false);
	const busyRef = useRef(false);

	const load = useCallback(async () => {
		if (busyRef.current) return; // don't overwrite what the user is in the middle of changing
		try {
			const res = await axios.get(`/scoring/${event_id}`, { params: { show } });
			if (busyRef.current) return;
			setImages(res.data);
		} catch (error) {
			console.error(error);
		}
	}, [event_id, show]);

	useEffect(() => {
		setImages(null);
		load();
		const timer = setInterval(load, 8000);
		return () => clearInterval(timer);
	}, [load]);

	// The photo on screen is looked up by id in the latest list, so hearts/points stay in step with the server.
	const focused = images && (images.find((i) => i.gte_id === focusedId) || images[0] || null);

	// Open the slider on the photo's current points (or 1) whenever a different photo is selected.
	const focusedKey = focused ? focused.gte_id : null;
	const focusedReward = focused ? focused.reward_given : 0;
	useEffect(() => {
		if (focusedKey !== null) setScore(focusedReward > 0 ? focusedReward : 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusedKey]);

	const run = async (work) => {
		busyRef.current = true;
		setBusy(true);
		try {
			await work();
		} catch (error) {
			console.error(error);
			alert('That did not save: ' + (error?.response?.data?.error || error.message));
		} finally {
			busyRef.current = false;
			setBusy(false);
		}
	};

	const refresh = async () => {
		const res = await axios.get(`/scoring/${event_id}`, { params: { show } });
		setImages(res.data);
		return res.data;
	};

	// Next photo after `current` in the list that still has no points (falls back to simply the next one).
	const nextAfter = (list, current) => {
		const at = list.findIndex((i) => i.gte_id === current.gte_id);
		const after = [...list.slice(at + 1), ...list.slice(0, Math.max(at, 0))];
		return after.find((i) => !i.reward_given) || after[0] || null;
	};

	const saveScore = () =>
		run(async () => {
			await axios.post('/scoring/score', { score: Number(score), gte_id: focused.gte_id });
			const list = await refresh();
			const next = nextAfter(list, focused);
			if (next && !focused.reward_given) setFocusedId(next.gte_id); // keep the photo on screen when just re-scoring
		});

	const toggleHeart = () =>
		run(async () => {
			await axios.post('/scoring/present', { gte_id: focused.gte_id, present: !focused.presentation_worthy });
			await refresh();
		});

	const toggleArchive = () =>
		run(async () => {
			const next = nextAfter(images, focused);
			await axios.post('/scoring/archive', { gte_id: focused.gte_id, archived: !focused.scoring_archived });
			const list = await refresh();
			if (show !== 'all') setFocusedId(next && list.some((i) => i.gte_id === next.gte_id) ? next.gte_id : null);
		});

	const count = images ? images.length : 0;
	const filters = [
		['queue', 'To score'],
		['archived', 'Archived'],
		['all', 'All']
	];

	return (
		<Container>
			<h1>
				Scoring {focused?.game_name}
				<Link to={`/event/${event_id}`}>
					<Button color="secondary" outline className="float-right mt-2">
						<i className="fa fa-arrow-left" aria-hidden="true"></i> Return
					</Button>
				</Link>
			</h1>
			<hr />
			<ButtonGroup className="mb-3">
				{filters.map(([key, label]) => (
					<Button key={key} outline={show !== key} color="primary" onClick={() => setShow(key)}>
						{label}
						{show === key && images ? ` (${count})` : ''}
					</Button>
				))}
			</ButtonGroup>

			{!images ? (
				<Spinner text="Checking for images" />
			) : !focused ? (
				<div>
					{show === 'queue' && 'Nothing left to score - come back later or wait on this page.'}
					{show === 'archived' && 'Nothing has been archived yet.'}
					{show === 'all' && 'No photos have been submitted yet.'}
				</div>
			) : (
				<>
					<Row>
						<Col md={8} style={{ textAlign: 'center' }}>
							<img alt="" style={{ maxWidth: '100%', maxHeight: '450px', objectFit: 'contain' }} src={focused.entry_image} />
							<br />
							Posted {moment.unix(focused.submitted_timestamp).fromNow()}
						</Col>
						<Col md={4}>
							<i
								onClick={() => !busy && toggleHeart()}
								title={focused.presentation_worthy ? 'In the slideshow - click to remove' : 'Click to add to the slideshow'}
								className={'fa-heart float-right ' + (focused.presentation_worthy ? 'fas' : 'far')}
								style={{ fontSize: '3em', cursor: 'pointer', color: focused.presentation_worthy ? '#e83e8c' : '', opacity: busy ? 0.5 : 1 }}
							/>
							<h3>{focused.task_title}</h3>
							{focused.task_description}
							<hr />
							<Input disabled={busy} min={0} max={focused.reward} type="range" value={score} onChange={(e) => setScore(e.target.value)} />
							<Button disabled={busy} onClick={saveScore} className="mt-3" color="primary">
								{focused.reward_given ? 'Change to' : 'Score'} {score} points for {focused.game_name} {busy && <i className="fas fa-sync fa-spin" />}
							</Button>
							{focused.reward_given > 0 && (
								<div className="mt-2">
									<Badge color="success">Currently {focused.reward_given} points</Badge>
								</div>
							)}
							<hr />
							<Button disabled={busy} onClick={toggleArchive} outline color="secondary">
								<i className={'fa fa-fw ' + (focused.scoring_archived ? 'fa-undo' : 'fa-archive')} />{' '}
								{focused.scoring_archived ? 'Move back to "To score"' : 'Archive (done with this one)'}
							</Button>
						</Col>
					</Row>
					<div
						style={{
							border: '2px solid black',
							minHeight: '100px',
							marginTop: '30px',
							overflowY: 'hidden',
							overflowX: 'scroll',
							whiteSpace: 'nowrap'
						}}
					>
						{images.map((img) => {
							const selected = img.gte_id === focused.gte_id;
							return (
								<div key={img.gte_id} style={{ display: 'inline-block', textAlign: 'center', verticalAlign: 'top', position: 'relative' }} className="my-2 mx-2">
									<img
										alt=""
										style={{ maxHeight: '160px', cursor: 'pointer', border: selected ? '3px solid blue' : '3px solid transparent', opacity: img.scoring_archived ? 0.6 : 1 }}
										onClick={() => setFocusedId(img.gte_id)}
										src={img.entry_image}
									/>
									{img.presentation_worthy && (
										<i className="fas fa-heart" style={{ position: 'absolute', top: 8, right: 8, color: '#e83e8c', fontSize: '1.4em', textShadow: '0 0 3px #fff' }} />
									)}
									<br />
									<Badge color="primary">{img.game_name}</Badge>{' '}
									{img.reward_given > 0 && <Badge color="success">{img.reward_given} pts</Badge>}
								</div>
							);
						})}
					</div>
				</>
			)}
		</Container>
	);
}
